import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by selecting the variant's media in slideshow.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */

  #handleVariantUpdate = (event) => {
    const variantResource = event.detail.resource;

    if (!variantResource || !this.slideshow) return;

    // Get the featured media ID from the variant
    const featuredMediaId = variantResource.featured_media?.id;

    // Update [hidden] before select(), matching the existing
    // slideshow[initial-slide] convention above (see the attributeChangedCallback
    // comment) of never selecting a slide before its [hidden] state is current.
    this.#updateVariantMediaVisibility(featuredMediaId);

    if (featuredMediaId) {
      // Select the variant's featured media in the slideshow
      this.slideshow.select({ id: featuredMediaId }, undefined, { animate: false });
    }
  };

  /**
   * Cleaner galleries: hides slides/thumbnails belonging to other colors,
   * keeping only the active variant's own media (plus any shared,
   * non-variant media, which is never marked and so is never touched here).
   * Slides/thumbnails carry a stable `slide-id`/`data-media-id` (both set to
   * the Shopify media id) regardless of hidden state, so this only ever
   * toggles the `hidden` attribute - it never removes/reorders anything the
   * slideshow's own index-based navigation depends on.
   *
   * @param {string | number | undefined} featuredMediaId - The newly selected variant's featured media id.
   */
  #updateVariantMediaVisibility(featuredMediaId) {
    if (!featuredMediaId) return;

    const id = String(featuredMediaId);

    for (const element of this.querySelectorAll('[data-variant-media="true"]')) {
      const mediaId = element.getAttribute('slide-id') ?? element.getAttribute('data-media-id');
      element.toggleAttribute('hidden', mediaId !== id);
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
